import React from 'react';
import styled, { css } from 'styled-components';
import { fadeInUp } from '../../styles/animations';
import { pretendardFont } from '../../styles/typography';

export const Section = styled.section<{ $bg?: 'white' | 'black' }>`
  width: 100%;
  background: ${({ $bg }) => ($bg === 'black' ? '#111111' : '#ffffff')};
`;

export const SectionInner = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0 20px;
  }
`;

export const SectionLabel = styled.span`
  ${pretendardFont}
  display: inline-block;
  border-radius: 99px;
  color: #9db510;
  border: 1px solid #ceda87;
  padding: 8px 28px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 16px;
`;

export const SectionTitle = styled.h2<{ $light?: boolean }>`
  ${pretendardFont}
  color: ${({ $light }) => ($light ? '#ffffff' : '#111111')};
  font-size: 48px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -1.2px;
  white-space: pre-line;
  margin-bottom: 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 32px;
    letter-spacing: -0.8px;
  }
`;

export const PillButton = styled.button<{ $variant?: 'lime' | 'black' }>`
  ${pretendardFont}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 28px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  text-align: center;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%; /* 28px */
  letter-spacing: -0.5px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 16px;
    padding: 12px 24px;
  }

  transition:
    transform 0.2s ease,
    opacity 0.2s ease;

  ${({ $variant = 'lime' }) =>
    $variant === 'lime'
      ? css`
          background: ${({ theme }) => theme.colors.lime};
          color: #111111;
        `
      : css`
          background: #111111;
          color: #d3ee34;
        `}

  &:hover {
    transform: translateY(-2px);
    opacity: 0.92;
  }
`;

export const Reveal = styled.div<{ $visible: boolean; $delay?: number }>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  animation: ${({ $visible }) => ($visible ? fadeInUp : 'none')} 0.7s ease
    forwards;
  animation-delay: ${({ $delay = 0 }) => `${$delay}s`};
`;

export const MarqueeTrack = styled.div<{ $reverse?: boolean }>`
  display: flex;
  width: max-content;
  animation: ${({ $reverse }) =>
    $reverse
      ? 'marqueeRight 30s linear infinite'
      : 'marqueeLeft 30s linear infinite'};

  @keyframes marqueeLeft {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }

  @keyframes marqueeRight {
    0% {
      transform: translateX(-50%);
    }
    100% {
      transform: translateX(0);
    }
  }
`;

export const MarqueeWrapper = styled.div`
  overflow: hidden;
  width: 100%;
  pointer-events: none;
`;

export const MarqueeImage = styled.img`
  height: 48px;
  width: auto;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 36px;
  }
`;

interface MarqueeTapeProps {
  src: string;
  reverse?: boolean;
}

export const MarqueeTape: React.FC<MarqueeTapeProps> = ({ src, reverse }) => (
  <MarqueeWrapper>
    <MarqueeTrack $reverse={reverse}>
      {Array.from({ length: 6 }).map((_, i) => (
        <MarqueeImage key={i} src={src} alt="" aria-hidden />
      ))}
    </MarqueeTrack>
  </MarqueeWrapper>
);
